import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  CheckSquare2,
  CircleDollarSign,
  ClipboardPenLine,
  Download,
  MapPin,
  PackageCheck,
  Plus,
  Search,
  ShoppingBag,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { StorePicker } from '../../components/StorePicker';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import { campaignBelongsToWorkspace } from '../../lib/workspace';
import { SalesSampleDetails } from './SalesSampleDetails';
import { loadOptions } from './manualSalesOptions';
import { correctionDeals, sampleAllowance, sampleDraft, sampleKey, samplePayload, sampleValidation } from './salesCorrectionSamples';
import type {
  Campaign,
  CampaignProduct,
  OperationalExceptionResponse,
  PaginatedResponse,
  Product,
  SalesBulkApprovalResponse,
  SalesDailySummary,
  SalesReviewDecision,
  SalesReviewResponse,
  SalesReviewSubmission,
  Venue,
} from '../../types/domain';

interface SalesFilters {
  search: string;
  client: string;
  agency: string;
  campaign: string;
  venue: string;
  brandAmbassador: string;
  product: string;
  reviewStatus: string;
  dateFrom: string;
  dateTo: string;
}

interface CorrectionLineDraft {
  product_id: string;
  product_name: string;
  product_sku: string;
  quantity: string;
  unit_price: string;
}

type NoSalesReason = Exclude<SalesReviewSubmission['source_submissions'][number]['no_sales_reason'], ''>;

interface DecisionDraft {
  submission: SalesReviewSubmission;
  decision: 'approved' | 'rejected' | 'corrected';
  correctionOutcome: 'product_lines' | 'no_sales';
  correctedNoSalesReason: NoSalesReason | '';
  note: string;
  lines: CorrectionLineDraft[];
  samples: Record<string, string>;
}

const emptyFilters: SalesFilters = {
  search: '',
  client: '',
  agency: '',
  campaign: '',
  venue: '',
  brandAmbassador: '',
  product: '',
  reviewStatus: '',
  dateFrom: '',
  dateTo: '',
};

const salesTableTabs = [
  { id: 'daily-product-summary', label: 'Daily product summary' },
  { id: 'sales-reports', label: 'Sales reports' },
  { id: 'exceptions', label: 'Exceptions' },
] as const;

type SalesTableTab = typeof salesTableTabs[number]['id'];

function filtersFromSearchParams(searchParams: URLSearchParams): SalesFilters {
  return {
    search: searchParams.get('search') ?? '',
    client: searchParams.get('client') ?? '',
    agency: searchParams.get('agency') ?? '',
    campaign: searchParams.get('campaign') ?? '',
    venue: searchParams.get('venue') ?? '',
    brandAmbassador: searchParams.get('brand_ambassador') ?? '',
    product: searchParams.get('product') ?? '',
    reviewStatus: searchParams.get('review_status') ?? '',
    dateFrom: searchParams.get('date_from') ?? '',
    dateTo: searchParams.get('date_to') ?? '',
  };
}

const dateTimeFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'Asia/Karachi',
});

const currencyFormatter = new Intl.NumberFormat('en-PK', {
  style: 'currency',
  currency: 'PKR',
  maximumFractionDigits: 2,
});

function formatValue(value: string | null) {
  return value == null ? 'Price unavailable' : currencyFormatter.format(Number(value));
}

const noSalesReasonLabels: Record<NoSalesReason, string> = {
  no_customer_demand: 'No customer demand',
  out_of_stock: 'Out of stock',
  store_closed: 'Store closed',
  product_unavailable: 'Product unavailable',
  other: 'Other',
};

function formatNoSalesReason(reason: SalesReviewSubmission['source_submissions'][number]['no_sales_reason']) {
  return reason ? noSalesReasonLabels[reason] : 'Reason not available';
}

function noSalesSources(report: SalesReviewSubmission) {
  return report.source_submissions.filter((source) => source.submission_type === 'no_sales');
}

function correctedNoSalesReason(report: SalesReviewSubmission) {
  const latestDecision = report.review_decisions[0];
  return report.review_status === 'corrected'
    ? latestDecision?.corrected_no_sales_reason ?? ''
    : '';
}

function toCorrectionLine(line: SalesReviewSubmission['effective_lines'][number]): CorrectionLineDraft {
  return {
    product_id: line.product_id,
    product_name: line.product_name,
    product_sku: line.product_sku,
    quantity: String(line.quantity),
    unit_price: line.unit_price ?? '',
  };
}

export function SalesPage() {
  const { accessToken, capabilities, selectedWorkspace } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const canReview = capabilities.includes('sales:review');
  const requestedTab = searchParams.get('tab');
  const activeTableTab = salesTableTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab as SalesTableTab
    : 'daily-product-summary';
  const [filters, setFilters] = useState<SalesFilters>(
    () => filtersFromSearchParams(searchParams),
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    () => filtersFromSearchParams(searchParams).search.trim(),
  );
  const [page, setPage] = useState(1);
  const [summaryPage, setSummaryPage] = useState(1);
  const [selectedSubmission, setSelectedSubmission] = useState<SalesReviewSubmission | null>(null);
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [bulkApprovalDraft, setBulkApprovalDraft] = useState<{
    reportIds: string[];
    idempotencyKey: string;
  } | null>(null);
  const [bulkApprovalError, setBulkApprovalError] = useState<string | null>(null);
  const [decisionDraft, setDecisionDraft] = useState<DecisionDraft | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [productToAdd, setProductToAdd] = useState('');
  const reportSamples = selectedSubmission?.source_submissions.flatMap((source) => source.samples ?? []) ?? [];

  const campaigns = useApiCollection<Campaign>(
    'sales-campaigns',
    '/campaigns/?page_size=100&ordering=-starts_on',
    accessToken,
  );
  const venues = useApiCollection<Venue>(
    'sales-venues',
    '/venues/?page_size=100&ordering=name',
    accessToken,
  );
  const products = useApiCollection<Product>(
    'sales-products',
    '/products/?page_size=200&ordering=name',
    accessToken,
  );
  const campaignOptions = useMemo(
    () => (campaigns.data?.results ?? []).filter((campaign) => (
      campaignBelongsToWorkspace(campaign, selectedWorkspace)
    )),
    [campaigns.data?.results, selectedWorkspace],
  );
  const clientOptions = useMemo(
    () => [...new Map(campaignOptions.map((campaign) => [
      campaign.client,
      { id: campaign.client, name: campaign.client_name },
    ])).values()],
    [campaignOptions],
  );
  const agencyOptions = useMemo(
    () => [...new Map(campaignOptions.flatMap((campaign) => (
      campaign.operating_agency
        ? [[campaign.operating_agency, {
            id: campaign.operating_agency,
            name: campaign.operating_agency_name ?? 'Agency',
          }] as const]
        : []
    ))).values()],
    [campaignOptions],
  );

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSearch(filters.search.trim()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  const reviewPath = (() => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: '25',
      ordering: '-business_date',
    });
    if (selectedWorkspace?.organizationId) params.set('workspace', selectedWorkspace.organizationId);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filters.client) params.set('client', filters.client);
    if (filters.agency) params.set('agency', filters.agency);
    if (filters.campaign) params.set('campaign', filters.campaign);
    if (filters.venue) params.set('venue', filters.venue);
    if (filters.brandAmbassador) params.set('brand_ambassador', filters.brandAmbassador);
    if (filters.product) params.set('product', filters.product);
    if (filters.reviewStatus) params.set('review_status', filters.reviewStatus);
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    return `/sales/review/?${params.toString()}`;
  })();
  const sales = useQuery({
    queryKey: ['sales-review', reviewPath, accessToken],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<SalesReviewResponse>(
      reviewPath,
      accessToken ? { accessToken } : {},
    ),
  });
  const exportParams = new URLSearchParams(reviewPath.split('?')[1]);
  exportParams.delete('page');
  exportParams.delete('page_size');
  const exportPath = `/sales/review/export/?${exportParams.toString()}`;
  const dailySummaryParams = new URLSearchParams(exportParams);
  dailySummaryParams.delete('ordering');
  dailySummaryParams.set('page', String(summaryPage));
  dailySummaryParams.set('page_size', '25');
  const dailySummaryPath = `/sales/review/daily-summary/?${dailySummaryParams.toString()}`;
  const dailySummary = useQuery({
    queryKey: ['sales-daily-summary', dailySummaryPath, accessToken],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<PaginatedResponse<SalesDailySummary>>(
      dailySummaryPath,
      accessToken ? { accessToken } : {},
    ),
  });
  const exceptionPath = (() => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: '25',
      exception_type: 'attendance_complete_missing_sales',
    });
    if (selectedWorkspace?.organizationId) params.set('workspace', selectedWorkspace.organizationId);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filters.client) params.set('client', filters.client);
    if (filters.agency) params.set('agency', filters.agency);
    if (filters.campaign) params.set('campaign', filters.campaign);
    if (filters.venue) params.set('venue', filters.venue);
    if (filters.brandAmbassador) params.set('brand_ambassador', filters.brandAmbassador);
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    return `/operational-exceptions/?${params.toString()}`;
  })();
  const exceptions = useQuery({
    queryKey: ['operational-exceptions', exceptionPath, accessToken],
    enabled: Boolean(accessToken) && activeTableTab === 'exceptions',
    queryFn: () => apiRequest<OperationalExceptionResponse>(
      exceptionPath,
      accessToken ? { accessToken } : {},
    ),
  });
  const campaignProducts = useQuery({
    queryKey: ['sales-correction-products', decisionDraft?.submission.campaign_id, decisionDraft?.submission.campaign_location_id, decisionDraft?.submission.business_date, accessToken],
    enabled: Boolean(
      accessToken
      && decisionDraft?.decision === 'corrected'
      && decisionDraft.correctionOutcome === 'product_lines',
    ),
    queryFn: ({ signal }) => loadOptions<CampaignProduct>(
      `/campaign-products/?campaign=${decisionDraft?.submission.campaign_id ?? ''}&effective_on=${decisionDraft?.submission.business_date ?? ''}${decisionDraft?.submission.campaign_location_id ? `&location=${decisionDraft.submission.campaign_location_id}` : ''}`,
      accessToken ?? '', signal,
    ),
  });
  const rows = sales.data?.results ?? [];
  const exceptionRows = exceptions.data?.results ?? [];
  const summary = sales.data?.summary ?? {
    total: 0,
    pending: 0,
    approved: 0,
    corrected: 0,
    rejected: 0,
    accepted_units: 0,
    accepted_value: '0.00',
  };
  const availableCorrectionProducts = (campaignProducts.data ?? []).filter(
    (item) => item.product_is_active !== false && !decisionDraft?.lines.some((line) => line.product_id === item.product),
  );
  const correctionSampleDeals = decisionDraft ? correctionDeals(decisionDraft.submission, campaignProducts.data ?? []) : [];
  const sampleError = decisionDraft?.correctionOutcome === 'product_lines'
    ? sampleValidation(correctionSampleDeals, decisionDraft.samples, decisionDraft.lines) : null;
  const eligibleReportIds = rows.filter((report) => (
    canReview && report.can_decide && report.review_status === 'pending'
  )).map((report) => report.id);
  const selectedEligibleReportIds = selectedReportIds.filter((id) => (
    eligibleReportIds.includes(id)
  ));
  const allEligibleSelected = eligibleReportIds.length > 0
    && eligibleReportIds.every((id) => selectedEligibleReportIds.includes(id));

  const decisionMutation = useMutation({
    mutationFn: (draft: DecisionDraft) => apiRequest<SalesReviewDecision>(
      `/sales/review/${draft.submission.id}/decisions/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          decision: draft.decision,
          note: draft.note,
          workspace: selectedWorkspace?.organizationId ?? null,
          idempotency_key: crypto.randomUUID(),
          ...(draft.decision === 'corrected' ? {
            ...(draft.correctionOutcome === 'no_sales' ? {
              corrected_no_sales_reason: draft.correctedNoSalesReason,
              samples: [],
            } : {
              lines: draft.lines.map((line) => ({
                product_id: line.product_id,
                quantity: Number(line.quantity),
                unit_price: line.unit_price === '' ? null : line.unit_price,
              })),
              samples: samplePayload(draft.samples),
            }),
          } : {}),
        }),
      },
    ),
    onSuccess: async () => {
      setDecisionDraft(null);
      setDecisionError(null);
      setSelectedSubmission(null);
      setProductToAdd('');
      await queryClient.invalidateQueries({ queryKey: ['sales-review'] });
    },
    onError: (error) => setDecisionError(getApiErrorMessage(error)),
  });
  const bulkApprovalMutation = useMutation({
    mutationFn: (draft: NonNullable<typeof bulkApprovalDraft>) => (
      apiRequest<SalesBulkApprovalResponse>(
        '/sales/review/bulk-approve/',
        {
          ...(accessToken ? { accessToken } : {}),
          method: 'POST',
          body: JSON.stringify({
            report_ids: draft.reportIds,
            workspace: selectedWorkspace?.organizationId ?? null,
            idempotency_key: draft.idempotencyKey,
          }),
        },
      )
    ),
    onSuccess: async () => {
      setBulkApprovalDraft(null);
      setBulkApprovalError(null);
      setSelectedReportIds([]);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sales-review'] }),
        queryClient.invalidateQueries({ queryKey: ['sales-daily-summary'] }),
      ]);
    },
    onError: (error) => setBulkApprovalError(getApiErrorMessage(error)),
  });
  const exportMutation = useMutation({
    mutationFn: () => downloadApiFile(
      exportPath,
      accessToken ? { accessToken } : {},
    ),
  });

  function updateFilter<Key extends keyof SalesFilters>(key: Key, value: SalesFilters[Key]) {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
    setSummaryPage(1);
    setSelectedReportIds([]);
  }

  function selectTableTab(tab: SalesTableTab) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setPage(1);
    setSummaryPage(1);
    setSelectedReportIds([]);
    setSearchParams(nextParams);
  }

  function toggleReportSelection(reportId: string) {
    setSelectedReportIds((current) => (
      current.includes(reportId)
        ? current.filter((id) => id !== reportId)
        : [...current, reportId]
    ));
  }

  function toggleAllEligible() {
    setSelectedReportIds(allEligibleSelected ? [] : eligibleReportIds);
  }

  function openBulkApproval() {
    if (selectedEligibleReportIds.length === 0) return;
    setBulkApprovalError(null);
    setBulkApprovalDraft({
      reportIds: selectedEligibleReportIds,
      idempotencyKey: crypto.randomUUID(),
    });
  }

  function openDecision(
    submission: SalesReviewSubmission,
    decision: DecisionDraft['decision'],
  ) {
    setDecisionError(null);
    setProductToAdd('');
    setSelectedSubmission(null);
    setDecisionDraft({
      submission,
      decision,
      correctionOutcome: 'product_lines',
      correctedNoSalesReason: '',
      note: '',
      samples: decision === 'corrected' ? sampleDraft(submission) : {},
      lines: decision === 'corrected'
        ? submission.effective_lines.map(toCorrectionLine)
        : [],
    });
  }

  function addCorrectionProduct() {
    const product = availableCorrectionProducts.find((item) => item.product === productToAdd);
    if (!product) return;
    setDecisionDraft((current) => current ? {
      ...current,
      lines: [
        ...current.lines,
        {
          product_id: product.product,
          product_name: product.product_name,
          product_sku: product.product_sku,
          quantity: '1',
          unit_price: product.product_unit_price ?? '',
        },
      ],
    } : null);
    setProductToAdd('');
  }

  function updateCorrectionLine(index: number, field: 'quantity' | 'unit_price', value: string) {
    setDecisionDraft((current) => current ? {
      ...current,
      lines: current.lines.map((line, lineIndex) => (
        lineIndex === index ? { ...line, [field]: value } : line
      )),
    } : null);
  }

  function removeCorrectionLine(index: number) {
    setDecisionDraft((current) => current ? {
      ...current,
      lines: current.lines.filter((_, lineIndex) => lineIndex !== index),
      samples: Object.fromEntries(Object.entries(current.samples).filter(([key]) => (
        !correctionSampleDeals.some((deal) => deal.parent === current.lines[index]?.product_id && key.startsWith(`${deal.id}:`))
      ))),
    } : null);
  }

  const correctionInvalid = decisionDraft?.decision === 'corrected' && (
    !decisionDraft.note.trim()
    || Boolean(sampleError)
    || (decisionDraft.correctionOutcome === 'no_sales'
      ? !decisionDraft.correctedNoSalesReason
      : decisionDraft.lines.length === 0
        || decisionDraft.lines.some((line) => !Number.isInteger(Number(line.quantity)) || Number(line.quantity) < 1 || Number(line.quantity) > 1_000_000 || (line.unit_price !== '' && (!Number.isFinite(Number(line.unit_price)) || Number(line.unit_price) < 0))))
  );

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>Sales</h1>
          <p>Review one consolidated report per ambassador, campaign, store, and business day.</p>
        </div>
        <div className="heading-actions">
          {activeTableTab !== 'exceptions' ? <button className="button secondary export-button" disabled={exportMutation.isPending || summary.total === 0} onClick={() => exportMutation.mutate()} type="button">
            <Download size={16} /> {exportMutation.isPending ? 'Preparing XLSX…' : 'Export XLSX'}
          </button> : null}
          <span className="record-count"><ShoppingBag size={16} /> {activeTableTab === 'exceptions' ? `${exceptions.data?.count ?? 0} exceptions` : `${summary.total} reports`}</span>
        </div>
      </header>
      {activeTableTab !== 'exceptions' && exportMutation.isError ? <p className="form-error page-error" role="alert">{getApiErrorMessage(exportMutation.error)}</p> : null}
      {bulkApprovalMutation.isError && !bulkApprovalDraft ? <p className="form-error page-error" role="alert">{bulkApprovalError}</p> : null}

      {activeTableTab !== 'exceptions' ? <section className="metric-grid sales-metrics" aria-label="Sales summary">
        <article className="metric-card"><span className="icon-chip"><ClipboardPenLine size={18} /></span><p>Submitted reports</p><strong>{summary.total}</strong><small>Matching the current filters</small></article>
        <article className="metric-card"><span className="icon-chip attendance-warning"><ShoppingBag size={18} /></span><p>Pending review</p><strong>{summary.pending}</strong><small>Awaiting an operator decision</small></article>
        <article className="metric-card"><span className="icon-chip"><PackageCheck size={18} /></span><p>Accepted units</p><strong>{summary.accepted_units}</strong><small>{summary.approved} approved · {summary.corrected} corrected</small></article>
        <article className="metric-card"><span className="icon-chip"><CircleDollarSign size={18} /></span><p>Accepted value</p><strong>{formatValue(summary.accepted_value)}</strong><small>{summary.rejected} rejected reports excluded</small></article>
      </section> : null}

      <section className="content-card attendance-filter-card">
        <div className="card-heading">
          <div><h2>{activeTableTab === 'exceptions' ? 'Filter sales exceptions' : 'Filter sales'}</h2><p>{activeTableTab === 'exceptions' ? 'Find checked-in shifts with no sales or no-sales report after shift end.' : 'Results and totals use the same authorised workspace scope.'}</p></div>
          <button className="text-button" onClick={() => { setFilters(emptyFilters); setDebouncedSearch(''); setPage(1); setSummaryPage(1); setSelectedReportIds([]); }} type="button">Reset filters</button>
        </div>
        <div className="attendance-filter-grid">
          <label className="attendance-search-filter">
            {activeTableTab === 'exceptions' ? 'BA, campaign or store' : 'BA, campaign, store, product or SKU'}
            <span><Search size={16} /><input aria-label={activeTableTab === 'exceptions' ? 'BA, campaign or store' : 'BA, campaign, store, product or SKU'} onChange={(event) => updateFilter('search', event.target.value)} placeholder={activeTableTab === 'exceptions' ? 'Search exceptions' : 'Search reports'} value={filters.search} /></span>
          </label>
          <label>Client<select aria-label="Sales client" onChange={(event) => updateFilter('client', event.target.value)} value={filters.client}><option value="">All clients</option>{clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label>
          <label>Agency<select aria-label="Sales agency" onChange={(event) => updateFilter('agency', event.target.value)} value={filters.agency}><option value="">All agencies</option>{agencyOptions.map((agency) => <option key={agency.id} value={agency.id}>{agency.name}</option>)}</select></label>
          <label>Campaign<select aria-label="Campaign" onChange={(event) => updateFilter('campaign', event.target.value)} value={filters.campaign}><option value="">All campaigns</option>{campaignOptions.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}</select></label>
          <label>Store<StorePicker allOptionLabel="All stores" ariaLabel="Store" onChange={(value) => updateFilter('venue', value)} options={(venues.data?.results ?? []).map((venue) => ({ id: venue.id, name: venue.name, address: venue.address, city: venue.city, chainName: venue.chain_name, storeCode: venue.store_code }))} value={filters.venue} /></label>
          {activeTableTab !== 'exceptions' ? <label>Product<select aria-label="Product" onChange={(event) => updateFilter('product', event.target.value)} value={filters.product}><option value="">All products</option>{(products.data?.results ?? []).map((product) => <option key={product.id} value={product.id}>{product.name} · {product.sku}</option>)}</select></label> : null}
          {activeTableTab !== 'exceptions' ? <label>Review<select aria-label="Review" onChange={(event) => updateFilter('reviewStatus', event.target.value)} value={filters.reviewStatus}><option value="">All review results</option><option value="pending">Pending</option><option value="pending_changes">Changes received</option><option value="approved">Approved</option><option value="corrected">Corrected</option><option value="rejected">Rejected</option></select></label> : null}
          <label>Date from<input max={filters.dateTo || undefined} onChange={(event) => updateFilter('dateFrom', event.target.value)} type="date" value={filters.dateFrom} /></label>
          <label>Date to<input min={filters.dateFrom || undefined} onChange={(event) => updateFilter('dateTo', event.target.value)} type="date" value={filters.dateTo} /></label>
        </div>
      </section>

      <nav aria-label="Sales data sections" className="campaign-setup-tabs sales-data-tabs" role="tablist">
        {salesTableTabs.map((tab) => (
          <button
            aria-controls={`sales-data-panel-${tab.id}`}
            aria-selected={activeTableTab === tab.id}
            className={activeTableTab === tab.id ? 'active' : ''}
            id={`sales-data-tab-${tab.id}`}
            key={tab.id}
            onClick={() => selectTableTab(tab.id)}
            role="tab"
            type="button"
          >
            <span>{tab.label}</span>
            <small>{tab.id === 'daily-product-summary' ? dailySummary.data?.count ?? 0 : tab.id === 'sales-reports' ? sales.data?.count ?? 0 : exceptions.data?.count ?? 0}</small>
          </button>
        ))}
      </nav>

      {activeTableTab === 'daily-product-summary' ? (
        <div aria-labelledby="sales-data-tab-daily-product-summary" id="sales-data-panel-daily-product-summary" role="tabpanel">
          <DataPanel loading={dailySummary.isLoading} error={dailySummary.isError} empty={(dailySummary.data?.results.length ?? 0) === 0}>
        <section className="content-card data-card daily-sales-summary-card">
          <div className="card-heading">
            <div><h2>Daily product summary</h2><p>Submitted and accepted quantities use the same authorised filters.</p></div>
            <span className="daily-sales-summary-count">{dailySummary.data?.count ?? 0} groups</span>
          </div>
          <div className="data-table-wrap">
            <table className="data-table daily-sales-summary-table">
              <thead><tr><th>Date</th><th>Campaign</th><th>Store</th><th>Brand ambassador</th><th>Product</th><th>Submitted</th><th>Accepted</th></tr></thead>
              <tbody>{(dailySummary.data?.results ?? []).map((item) => <tr key={`${item.business_date}-${item.campaign_id}-${item.venue_id}-${item.brand_ambassador_id}-${item.product_id}`}><td><strong>{item.business_date}</strong><small>{item.report_count} daily {item.report_count === 1 ? 'report' : 'reports'}</small></td><td><strong>{item.campaign_name}</strong></td><td><strong>{item.venue_name}</strong><small>{item.venue_city}</small></td><td><strong>{item.brand_ambassador_name}</strong></td><td><strong>{item.product_name}</strong><small>{item.product_sku}</small></td><td><strong>{item.original_units} units</strong><small>{formatValue(item.original_value)}</small></td><td><strong>{item.accepted_units} units</strong><small>{formatValue(item.accepted_value)} · {item.pending_reports} pending</small></td></tr>)}</tbody>
            </table>
          </div>
          <div className="attendance-pagination">
            <span>Page {summaryPage} · {dailySummary.data?.count ?? 0} matching groups</span>
            <div><button className="button secondary" disabled={!dailySummary.data?.previous} onClick={() => setSummaryPage((current) => Math.max(1, current - 1))} type="button">Previous</button><button className="button secondary" disabled={!dailySummary.data?.next} onClick={() => setSummaryPage((current) => current + 1)} type="button">Next</button></div>
          </div>
        </section>
          </DataPanel>
        </div>
      ) : null}

      {activeTableTab === 'sales-reports' ? (
        <div aria-labelledby="sales-data-tab-sales-reports" id="sales-data-panel-sales-reports" role="tabpanel">
          <DataPanel loading={sales.isLoading} error={sales.isError} empty={rows.length === 0}>
        <section className="content-card data-card sales-table-card">
          <div className="data-table-wrap">
            <table className="data-table sales-table">
              <thead>
                <tr>
                  <th><input aria-label="Select all eligible sales reports" checked={allEligibleSelected} disabled={eligibleReportIds.length === 0} onChange={toggleAllEligible} type="checkbox" /></th>
                  <th>Brand ambassador</th>
                  <th>Campaign and store</th>
                  <th>Submitted</th>
                  <th>Effective sales</th>
                  <th>Review</th>
                  <th>
                    <span className="sr-only">Actions</span>
                    {canReview ? <button className="button primary sales-bulk-approve-button" disabled={selectedEligibleReportIds.length === 0 || bulkApprovalMutation.isPending} onClick={openBulkApproval} type="button"><CheckSquare2 size={16} /> Approve selected ({selectedEligibleReportIds.length})</button> : null}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((submission) => (
                  <tr key={submission.id}>
                    <td><input aria-label={`Select ${submission.reference}`} checked={eligibleReportIds.includes(submission.id) && selectedReportIds.includes(submission.id)} disabled={!canReview || !submission.can_decide || submission.review_status !== 'pending'} onChange={() => toggleReportSelection(submission.id)} type="checkbox" /></td>
                    <td><strong>{submission.brand_ambassador_name}</strong><small>{submission.source_count} source {submission.source_count === 1 ? 'submission' : 'submissions'} · {submission.reviewer_organization_name}</small></td>
                    <td><strong>{submission.campaign_name}</strong><small><MapPin size={12} /> {submission.venue_name} · {submission.venue_city}</small></td>
                    <td><strong>{submission.reference}</strong><small>{submission.business_date} · last entry {dateTimeFormatter.format(new Date(submission.submitted_at))}</small></td>
                    <td><strong>{submission.effective_total_units === 0 && (noSalesSources(submission).length > 0 || correctedNoSalesReason(submission)) ? correctedNoSalesReason(submission) ? 'No sales confirmed' : 'No sales reported' : `${submission.effective_total_units} units`}</strong><small>{correctedNoSalesReason(submission) ? `Corrected to no sales · ${formatNoSalesReason(correctedNoSalesReason(submission))} · ${formatValue(submission.effective_total_value)}` : noSalesSources(submission).length > 0 ? `${noSalesSources(submission).length} no-sales ${noSalesSources(submission).length === 1 ? 'attestation' : 'attestations'} · ${formatValue(submission.effective_total_value)}` : formatValue(submission.effective_total_value)}</small></td>
                    <td><StatusPill value={submission.review_status} /><small>{submission.review_status === 'pending' ? 'Awaiting review' : submission.review_status === 'pending_changes' ? 'New entries require review' : 'Latest audited decision'}</small></td>
                    <td><button className="button secondary sales-review-button" onClick={() => setSelectedSubmission(submission)} type="button"><ClipboardPenLine size={15} /> {canReview ? 'Review report' : 'View report'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="attendance-pagination">
            <span>Page {page} · {sales.data?.count ?? 0} matching reports</span>
            <div><button className="button secondary" disabled={!sales.data?.previous} onClick={() => { setSelectedReportIds([]); setPage((current) => Math.max(1, current - 1)); }} type="button">Previous</button><button className="button secondary" disabled={!sales.data?.next} onClick={() => { setSelectedReportIds([]); setPage((current) => current + 1); }} type="button">Next</button></div>
          </div>
        </section>
          </DataPanel>
        </div>
      ) : null}

      {activeTableTab === 'exceptions' ? (
        <div aria-labelledby="sales-data-tab-exceptions" id="sales-data-panel-exceptions" role="tabpanel">
          <DataPanel loading={exceptions.isLoading} error={exceptions.isError} empty={exceptionRows.length === 0}>
            <section className="content-card data-card sales-table-card">
              <div className="card-heading">
                <div><h2>Checked in, sales report missing</h2><p>The shift has ended and the BA submitted no sales or no-sales report. Checkout status is shown separately and does not delay sales follow-up.</p></div>
                <span className="daily-sales-summary-count">{exceptions.data?.count ?? 0} exceptions</span>
              </div>
              <div className="data-table-wrap">
                <table className="data-table operational-exceptions-table">
                  <thead><tr><th>Brand ambassador</th><th>Campaign and store</th><th>Scheduled shift</th><th>Attendance</th><th>Sales report</th></tr></thead>
                  <tbody>{exceptionRows.map((item) => <tr key={`${item.assignment_id}-${item.shift_id}`}>
                    <td><strong>{item.brand_ambassador_name}</strong><small>{item.brand_ambassador_reference} · {item.assignment_reference}</small></td>
                    <td><strong>{item.campaign_name}</strong><small><MapPin size={12} /> {item.venue_name} · {item.venue_city}</small></td>
                    <td><strong>{dateTimeFormatter.format(new Date(item.shift_starts_at))}</strong><small>Ended {dateTimeFormatter.format(new Date(item.shift_ends_at))}</small></td>
                    <td><StatusPill value={item.has_effective_check_out ? 'approved' : 'pending'} /><small>{item.has_effective_check_out ? 'Effective check-in and checkout' : 'Effective check-in; checkout missing'}</small></td>
                    <td><StatusPill value="pending" /><small>Missing sales report</small></td>
                  </tr>)}</tbody>
                </table>
              </div>
              <div className="attendance-pagination">
                <span>Page {page} · {exceptions.data?.count ?? 0} matching exceptions</span>
                <div><button className="button secondary" disabled={!exceptions.data?.previous} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button><button className="button secondary" disabled={!exceptions.data?.next} onClick={() => setPage((current) => current + 1)} type="button">Next</button></div>
              </div>
            </section>
          </DataPanel>
        </div>
      ) : null}

      {bulkApprovalDraft ? (
        <div className="confirmation-backdrop" role="presentation">
          <section aria-labelledby="bulk-sales-approval-title" aria-modal="true" className="confirmation-dialog" role="dialog">
            <button aria-label="Close bulk approval" className="icon-button confirmation-close" disabled={bulkApprovalMutation.isPending} onClick={() => setBulkApprovalDraft(null)} type="button"><X size={19} /></button>
            <div className="confirmation-icon"><CheckSquare2 size={22} /></div>
            <h2 id="bulk-sales-approval-title">Approve selected sales reports</h2>
            <p>You are approving <strong>{bulkApprovalDraft.reportIds.length}</strong> clean pending daily {bulkApprovalDraft.reportIds.length === 1 ? 'report' : 'reports'}.</p>
            <p className="confirmation-note">The server will approve the complete selection transactionally. If any report changed, was already decided, or is outside your authority, none of the selected reports will be approved.</p>
            {bulkApprovalError ? <p className="form-error" role="alert">{bulkApprovalError}</p> : null}
            <div className="confirmation-actions"><button className="button secondary" disabled={bulkApprovalMutation.isPending} onClick={() => setBulkApprovalDraft(null)} type="button">Cancel</button><button className="button primary" disabled={bulkApprovalMutation.isPending} onClick={() => bulkApprovalMutation.mutate(bulkApprovalDraft)} type="button">{bulkApprovalMutation.isPending ? 'Approving…' : 'Confirm bulk approval'}</button></div>
          </section>
        </div>
      ) : null}

      {selectedSubmission ? (
        <div className="confirmation-backdrop" role="presentation">
          <section aria-labelledby="sales-detail-title" aria-modal="true" className="confirmation-dialog sales-detail-dialog" role="dialog">
            <button aria-label="Close sales report" className="icon-button confirmation-close" onClick={() => setSelectedSubmission(null)} type="button"><X size={19} /></button>
            <div className="attendance-evidence-heading"><div className="confirmation-icon"><ShoppingBag size={22} /></div><div><p className="eyebrow">{selectedSubmission.reference} · {selectedSubmission.business_date}</p><h2 id="sales-detail-title">Daily sales report</h2></div></div>
            <div className="sales-report-meta"><span><small>Brand ambassador</small><strong>{selectedSubmission.brand_ambassador_name}</strong></span><span><small>Campaign</small><strong>{selectedSubmission.campaign_name}</strong></span><span><small>Store</small><strong>{selectedSubmission.venue_name}</strong></span><span><small>Review</small><StatusPill value={selectedSubmission.review_status} /></span></div>
            {selectedSubmission.has_new_submissions ? <p className="confirmation-note"><strong>Changes received:</strong> new sales entries were added after the latest decision. Review the full daily total again.</p> : null}
            <div className="sales-line-section">
              <h3>Combined sales entries ({selectedSubmission.source_count})</h3>
              {selectedSubmission.lines.map((line) => (
                <div className="sales-line-row" key={line.id}>
                  <span><strong>{line.product_name}</strong><small>{line.product_sku}</small></span>
                  <span>{line.quantity}{line.unit_price == null ? ' · mixed prices' : ` × ${formatValue(line.unit_price)}`}</span>
                  <strong>{formatValue(line.line_total)}</strong>
                  <SalesSampleDetails
                    label={`Free samples given with ${line.product_name}`}
                    samples={reportSamples.filter((sample) => sample.qualifying_product_id === line.product_id)}
                  />
                </div>
              ))}
              {selectedSubmission.lines.length === 0 && noSalesSources(selectedSubmission).length > 0 ? <p className="confirmation-note"><strong>No sales reported:</strong> this daily report contains an explicit BA attestation rather than missing product data.</p> : null}
              <div className="sales-line-total"><span>{selectedSubmission.original_total_units} units</span><strong>{formatValue(selectedSubmission.original_total_value)}</strong></div>
            </div>
            {selectedSubmission.review_status === 'corrected' ? <div className="sales-line-section corrected">
              <h3>Effective corrected sales</h3>
              {selectedSubmission.effective_lines.length > 0 ? selectedSubmission.effective_lines.map((line) => <div className="sales-line-row" key={line.id}>
                <span><strong>{line.product_name}</strong><small>{line.product_sku}</small></span><span>{line.quantity} × {formatValue(line.unit_price)}</span><strong>{formatValue(line.line_total)}</strong>
                <SalesSampleDetails label={`Corrected free samples for ${line.product_name}`} samples={(selectedSubmission.effective_samples ?? []).filter((sample) => sample.qualifying_product_id === line.product_id)} />
              </div>) : <p className="confirmation-note"><strong>No sales confirmed:</strong> {formatNoSalesReason(correctedNoSalesReason(selectedSubmission))}</p>}
              <div className="sales-line-total"><span>{selectedSubmission.effective_total_units} units</span><strong>{formatValue(selectedSubmission.effective_total_value)}</strong></div>
            </div> : null}
            <div className="attendance-decision-history"><h3>Source submissions</h3>{selectedSubmission.source_submissions.map((source) => <article key={source.id}>
              <StatusPill value={source.collection_source === 'manual' ? 'manual' : source.submission_type === 'no_sales' ? 'no_sales' : 'submitted'} />
              <span><strong>{source.reference} · {source.submission_type === 'no_sales' ? formatNoSalesReason(source.no_sales_reason) : `${source.total_units} units · ${formatValue(source.total_value)}`}</strong>
                <small>{source.assignment_reference ?? 'Unassigned'}{source.shift_starts_at && source.shift_ends_at ? ` · shift ${dateTimeFormatter.format(new Date(source.shift_starts_at))}–${dateTimeFormatter.format(new Date(source.shift_ends_at))}` : ''} · represented {dateTimeFormatter.format(new Date(source.sold_at))} · submitted {dateTimeFormatter.format(new Date(source.created_at))}</small>
              </span>
              {source.collection_source === 'manual' ? <p><strong>Manually entered by {source.entered_by_name}</strong> · {source.import_reference}<br />Reason: {source.manual_entry_reason}</p> : null}
              {source.notes ? <p>{source.notes}</p> : null}
              <SalesSampleDetails label={`Free samples summary for ${source.reference}`} samples={source.samples ?? []} summary />
            </article>)}</div>
            {selectedSubmission.review_decisions.length > 0 ? <div className="attendance-decision-history"><h3>Decision history</h3>{selectedSubmission.review_decisions.map((decision) => <article key={decision.id}><StatusPill value={decision.decision} /><span><strong>{decision.reviewer_name}</strong><small>{decision.reviewer_organization_name ?? 'Platform-wide authority'} · {dateTimeFormatter.format(new Date(decision.created_at))}</small></span>{decision.corrected_no_sales_reason ? <p><strong>Corrected to no sales:</strong> {formatNoSalesReason(decision.corrected_no_sales_reason)}</p> : null}{decision.note ? <p>{decision.note}</p> : null}</article>)}</div> : null}
            <div className="confirmation-actions attendance-review-actions"><button className="button secondary" onClick={() => setSelectedSubmission(null)} type="button">Close</button>{selectedSubmission.can_decide && canReview ? <button className="button danger" onClick={() => openDecision(selectedSubmission, 'rejected')} type="button">Reject</button> : null}{selectedSubmission.can_decide && canReview ? <button className="button secondary" onClick={() => openDecision(selectedSubmission, 'corrected')} type="button">Correct</button> : null}{selectedSubmission.can_decide && canReview ? <button className="button primary" onClick={() => openDecision(selectedSubmission, 'approved')} type="button">Approve</button> : null}</div>
          </section>
        </div>
      ) : null}

      {decisionDraft ? (
        <div className="confirmation-backdrop" role="presentation">
          <section aria-labelledby="sales-decision-title" aria-modal="true" className={`confirmation-dialog ${decisionDraft.decision === 'corrected' ? 'sales-correction-dialog' : ''}`} role="dialog">
            <button aria-label="Close sales decision" className="icon-button confirmation-close" disabled={decisionMutation.isPending} onClick={() => setDecisionDraft(null)} type="button"><X size={19} /></button>
            <div className="confirmation-icon">{decisionDraft.decision === 'approved' ? <BadgeCheck size={22} /> : decisionDraft.decision === 'rejected' ? <XCircle size={22} /> : <ClipboardPenLine size={22} />}</div>
            <h2 id="sales-decision-title">{decisionDraft.decision === 'approved' ? 'Approve sales report' : decisionDraft.decision === 'rejected' ? 'Reject sales report' : 'Correct sales report'}</h2>
            <p><strong>{decisionDraft.submission.reference}</strong> · {decisionDraft.submission.brand_ambassador_name} at {decisionDraft.submission.venue_name}</p>
            {decisionDraft.decision === 'corrected' ? <>
              <label className="sales-correction-outcome">Correction outcome
                <select aria-label="Correction outcome" onChange={(event) => setDecisionDraft((current) => current ? { ...current, correctionOutcome: event.target.value as DecisionDraft['correctionOutcome'], correctedNoSalesReason: event.target.value === 'no_sales' ? current.correctedNoSalesReason : '' } : null)} value={decisionDraft.correctionOutcome}>
                  <option value="product_lines">Correct products and free samples</option><option value="no_sales">Correct to no sales</option>
                </select>
              </label>
              {decisionDraft.correctionOutcome === 'product_lines' ? <div className="sales-correction-lines">
                <div className="sales-add-product">
                  <label>Add sellable SKU
                    <select aria-label="Add campaign product" disabled={campaignProducts.isLoading || campaignProducts.isError} onChange={(event) => setProductToAdd(event.target.value)} value={productToAdd}>
                      <option value="">{campaignProducts.isLoading ? 'Loading campaign products…' : 'Select a campaign product'}</option>
                      {availableCorrectionProducts.map((item) => <option key={item.id} value={item.product}>{item.product_name} · {item.product_sku}</option>)}
                    </select>
                  </label>
                  <button className="button secondary" disabled={!productToAdd || campaignProducts.isError} onClick={addCorrectionProduct} type="button"><Plus size={15} /> Add product</button>
                </div>
                {campaignProducts.isError ? <p className="form-error" role="alert">Unable to load campaign products. <button className="button secondary" onClick={() => void campaignProducts.refetch()} type="button">Retry products</button></p> : null}
                {campaignProducts.isSuccess && availableCorrectionProducts.length === 0 ? <p className="inline-help">All active sellable campaign SKUs are already listed.</p> : null}
                {decisionDraft.lines.map((line, index) => <div className="sales-correction-line" key={line.product_id}>
                  <span><strong>{line.product_name}</strong><small>{line.product_sku}</small></span>
                  <label>Quantity<input aria-label={`${line.product_name} quantity`} min="1" step="1" onChange={(event) => updateCorrectionLine(index, 'quantity', event.target.value)} required type="number" value={line.quantity} /></label>
                  <label>Unit price (PKR)<input aria-label={`${line.product_name} unit price`} min="0" onChange={(event) => updateCorrectionLine(index, 'unit_price', event.target.value)} step="0.01" type="number" value={line.unit_price} /></label>
                  <button aria-label={`Remove ${line.product_name}`} className="icon-button" onClick={() => removeCorrectionLine(index)} type="button"><Trash2 size={16} /></button>
                  {correctionSampleDeals.filter((deal) => deal.parent === line.product_id).map((deal) => {
                    const allowance = sampleAllowance(deal, Number(line.quantity));
                    return <section aria-label={`Correct free samples for ${line.product_name}`} className="sales-correction-samples" key={deal.id}>
                      <strong>Free samples</strong>
                      <small>{allowance === null ? 'The server will validate the recorded deal allowance.' : `Choose up to ${allowance} in total for this product.`} Separate from paid sales.</small>
                      {deal.products.map((sample) => <label key={sample.id}>{sample.name}
                        <input aria-label={`${line.product_name}: ${sample.name} free sample quantity`} inputMode="numeric" type="number" min="0" max={allowance ?? undefined} step="1" placeholder="0" value={decisionDraft.samples[sampleKey(deal.id, sample.id)] ?? ''} onChange={(event) => setDecisionDraft((current) => current ? { ...current, samples: { ...current.samples, [sampleKey(deal.id, sample.id)]: event.target.value } } : null)} />
                      </label>)}
                    </section>;
                  })}
                </div>)}
              </div> : <section className="sales-correction-no-sales">
                <h3>Correct to no sales</h3><p>This replaces effective sales and free samples with zero. The original submission remains in the audit history.</p>
                <label>No-sales reason<select aria-label="No-sales correction reason" onChange={(event) => setDecisionDraft((current) => current ? { ...current, correctedNoSalesReason: event.target.value as NoSalesReason | '' } : null)} required value={decisionDraft.correctedNoSalesReason}><option value="">Select why no sales were made</option>{Object.entries(noSalesReasonLabels).map(([reason, label]) => <option key={reason} value={reason}>{label}</option>)}</select></label>
              </section>}
            </> : null}
            <label className="attendance-decision-note">Review note {decisionDraft.decision === 'approved' ? '(optional)' : '(required)'}<textarea autoFocus onChange={(event) => setDecisionDraft((current) => current ? { ...current, note: event.target.value } : null)} placeholder="Record how this decision was verified" rows={4} value={decisionDraft.note} /></label>
            <p className="confirmation-note">All original BA submissions remain unchanged. This creates a new audited decision for the complete daily total{decisionDraft.decision === 'corrected' ? ' with a replacement sales snapshot' : ''}.</p>
            {sampleError ? <p className="form-error" role="alert">{sampleError}</p> : null}
            {decisionError ? <p className="form-error" role="alert">{decisionError}</p> : null}
            <div className="confirmation-actions"><button className="button secondary" disabled={decisionMutation.isPending} onClick={() => setDecisionDraft(null)} type="button">Cancel</button><button className={decisionDraft.decision === 'rejected' ? 'button danger' : 'button primary'} disabled={decisionMutation.isPending || Boolean(correctionInvalid)} onClick={() => decisionMutation.mutate(decisionDraft)} type="button">{decisionMutation.isPending ? 'Saving…' : decisionDraft.decision === 'approved' ? 'Confirm approval' : decisionDraft.decision === 'rejected' ? 'Confirm rejection' : 'Save correction'}</button></div>
          </section>
        </div>
      ) : null}
    </>
  );
}
